import { useMemo } from 'react';
import { useSkillTaxonomy, type SkillTaxonomyRow } from '@/lib/queries';

/**
 * v1.6 §7.13 — datalist-backed input over the canonical SkillTaxonomy.
 *
 * The server-side canonicalizer (Step 29 for workflows) rewrites aliases → slugs at save
 * time and 400s on unknown slugs. This picker mirrors that behaviour at authoring time:
 * users can type either a slug ("python") or an alias ("python3") and see a live resolution
 * hint below the input. Free text is still allowed — the server has the final say — but the
 * taxonomy dropdown makes the expected values discoverable.
 *
 * Callers: WorkflowNew (satisfiedByBadge / emitsBadge skills), OpeningForm (required +
 * preferred skills), StudentSkills (future).
 *
 * `onResolve` optional callback fires whenever the value resolves to a known taxonomy row —
 * lets callers snap sibling fields (e.g. Badge taxonomy) into agreement.
 */
export function SkillPicker({
  value,
  onChange,
  onResolve,
  placeholder,
  listId,
}: {
  value: string;
  onChange: (v: string) => void;
  onResolve?: (row: SkillTaxonomyRow | null) => void;
  placeholder?: string;
  listId?: string;
}) {
  const q = useSkillTaxonomy();
  const rows = q.data?.rows ?? [];
  const id = listId ?? 'skill-taxonomy-datalist';

  const resolution = useMemo(() => {
    const raw = (value ?? '').trim().toLowerCase();
    if (!raw) return null;
    const bySlug = rows.find((r) => r.slug.toLowerCase() === raw);
    if (bySlug) return { canonical: bySlug.slug, viaAlias: false, row: bySlug };
    const byAlias = rows.find((r) => r.aliases.some((a) => a.toLowerCase() === raw));
    if (byAlias) return { canonical: byAlias.slug, viaAlias: true, row: byAlias };
    return null;
  }, [value, rows]);

  return (
    <>
      <input
        className="input"
        list={id}
        placeholder={placeholder ?? 'aptitude, dsa, python…'}
        value={value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v);
          if (onResolve) {
            const raw = v.trim().toLowerCase();
            const row = rows.find(
              (r) => r.slug.toLowerCase() === raw || r.aliases.some((a) => a.toLowerCase() === raw),
            ) ?? null;
            onResolve(row);
          }
        }}
      />
      <datalist id={id}>
        {rows.map((r) => (
          <option key={r._id} value={r.slug} label={`${r.displayName} · ${r.taxonomy}`} />
        ))}
      </datalist>
      {value ? (
        resolution ? (
          <span className="cell-sub" style={{ fontSize: 11 }}>
            {resolution.viaAlias ? `alias → ${resolution.canonical}` : `✓ canonical (${resolution.row.taxonomy})`}
          </span>
        ) : (
          <span className="cell-sub" style={{ fontSize: 11, color: 'rgb(var(--ink-warning))' }}>
            unknown skill — add to SkillTaxonomy first
          </span>
        )
      ) : null}
    </>
  );
}
